import type { CascaderOptionType } from "antd/es/cascader";
import {
  getCascaderOptionSearchText,
  matchesCascaderSearch,
  normalizeCascaderSearchText
} from "../../../../../shared/utility/cascader-search";

/**
 * What a node of an action picker's option tree is, as far as the add handlers are concerned.
 *
 * The numeric members are compared with `===` against `option.category`. A top-level category row
 * carries its `EActionCategory` as a numeric STRING instead ("2" for Custom), so it never collides
 * with `action` (2) here — keep it that way: the handlers depend on the same strict comparison.
 */
export enum EActionNodeCategory {
  event,
  actionCategory,
  action,
  actionFolder,
  triggerEventAction
}

/**
 * Presentation facts a builder attaches to a category option, because that option's `label` is an
 * element (icon + name + BETA + count) the popup cannot read its parts back out of.
 */
export interface IActionPickerOptionMeta {
  label: string;
  iconName: string;
  isBeta?: boolean;
  /** The Custom (user actions) branch, drawn in the orange accent down to its leaves. */
  isCustom?: boolean;
}

/** A picker option as the builders produce it — antd's shape plus the optional presentation facts. */
export interface IActionPickerOption extends CascaderOptionType {
  pickerMeta?: IActionPickerOptionMeta;
}

export enum EActionPickerNodeKind {
  /** A branch of the host's root: "Initial Actions", "onClick". Shown as a tab, not a row. */
  event = "event",
  /** A root-level leaf that adds at once — the screen's "Recurring Actions". */
  leafEvent = "leafEvent",
  category = "category",
  folder = "folder",
  /** A leaf the add handlers act on: an action, or a Trigger target event. */
  action = "action",
  /** A leaf nothing can be added from — a user action folder with no actions in it. */
  emptyFolder = "emptyFolder"
}

export interface IActionPickerNode {
  /** Unique within the tree: the chain of option values from the root. */
  key: string;
  /** The builder's own object, handed back to the add handler untouched. */
  option: IActionPickerOption;
  kind: EActionPickerNodeKind;
  label: string;
  iconName?: string;
  isBeta: boolean;
  isCustom: boolean;
  /** Addable leaves below this node (1 for an addable leaf, 0 for an empty folder). */
  leafCount: number;
  depth: number;
  parent: IActionPickerNode | null;
  children: IActionPickerNode[];
}

function hasChildren(option: CascaderOptionType): boolean {
  return Array.isArray(option.children) && option.children.length > 0;
}

function isAddableLeafCategory(category: unknown): boolean {
  return category === EActionNodeCategory.action || category === EActionNodeCategory.triggerEventAction;
}

function resolveKind(option: IActionPickerOption, depth: number): EActionPickerNodeKind {
  // An event stays an event even with nothing under it (no action rights): it is a tab with an empty
  // list, never a row that "adds" — its path would carry no action for the handler to add.
  if (depth === 0 && option.category === EActionNodeCategory.event) {
    return EActionPickerNodeKind.event;
  }
  if (hasChildren(option)) {
    if (option.pickerMeta) {
      return EActionPickerNodeKind.category;
    }
    return EActionPickerNodeKind.folder;
  }
  if (depth === 0 && option.category === EActionNodeCategory.action) {
    return EActionPickerNodeKind.leafEvent;
  }
  if (isAddableLeafCategory(option.category)) {
    return EActionPickerNodeKind.action;
  }
  return EActionPickerNodeKind.emptyFolder;
}

function toNode(option: IActionPickerOption, parent: IActionPickerNode | null, depth: number): IActionPickerNode {
  const kind: EActionPickerNodeKind = resolveKind(option, depth);
  const meta: IActionPickerOptionMeta | undefined = option.pickerMeta;
  const node: IActionPickerNode = {
    key: `${parent ? `${parent.key}\u0001` : ""}${String(option.value ?? "")}`,
    option,
    kind,
    label: meta?.label ?? getCascaderOptionSearchText(option),
    iconName: meta?.iconName,
    isBeta: meta?.isBeta === true,
    isCustom: meta?.isCustom === true || parent?.isCustom === true,
    leafCount: 0,
    depth,
    parent,
    children: []
  };
  const childOptions: IActionPickerOption[] = (option.children as IActionPickerOption[] | undefined) ?? [];
  node.children = childOptions.map((child: IActionPickerOption): IActionPickerNode => toNode(child, node, depth + 1));
  if (kind === EActionPickerNodeKind.action || kind === EActionPickerNodeKind.leafEvent) {
    node.leafCount = 1;
  } else {
    node.leafCount = node.children.reduce((sum: number, child: IActionPickerNode): number => sum + child.leafCount, 0);
  }
  return node;
}

/** The picker's view of a host's option tree. The options themselves are neither copied nor changed. */
export function toActionPickerNodes(options: IActionPickerOption[]): IActionPickerNode[] {
  return options.map((option: IActionPickerOption): IActionPickerNode => toNode(option, null, 0));
}

export function isAddableNode(node: IActionPickerNode): boolean {
  return node.kind === EActionPickerNodeKind.action || node.kind === EActionPickerNodeKind.leafEvent;
}

export function isBranchNode(node: IActionPickerNode): boolean {
  return (
    node.kind === EActionPickerNodeKind.event ||
    node.kind === EActionPickerNodeKind.category ||
    node.kind === EActionPickerNodeKind.folder
  );
}

/** Root-first chain of nodes ending at `node`. */
export function getNodeChain(node: IActionPickerNode): IActionPickerNode[] {
  const chain: IActionPickerNode[] = [];
  let cursor: IActionPickerNode | null = node;
  while (cursor) {
    chain.unshift(cursor);
    cursor = cursor.parent;
  }
  return chain;
}

/**
 * The exact `selectedOptions` antd's Cascader handed the add handlers for this leaf: the builders'
 * own option objects, from the host's root down to the leaf. When the root is an event level, the
 * event is its first element — the Trigger branch reads the target component and event by position
 * (`selectedOptions[2]`, `[3]`), so nothing may be inserted or dropped.
 */
export function getSelectedOptionsPath(node: IActionPickerNode): CascaderOptionType[] {
  return getNodeChain(node).map((link: IActionPickerNode): CascaderOptionType => link.option);
}

/** The top-level category a node sits in (itself when it is one), or null above the categories. */
export function getCategoryAncestor(node: IActionPickerNode): IActionPickerNode | null {
  let cursor: IActionPickerNode | null = node;
  while (cursor) {
    if (cursor.kind === EActionPickerNodeKind.category) {
      return cursor;
    }
    cursor = cursor.parent;
  }
  return null;
}

export function collectAddableLeaves(nodes: IActionPickerNode[], into: IActionPickerNode[] = []): IActionPickerNode[] {
  for (const node of nodes) {
    if (isAddableNode(node)) {
      into.push(node);
    } else {
      collectAddableLeaves(node.children, into);
    }
  }
  return into;
}

/**
 * One searchable leaf with its text prepared once per open, not once per keystroke. Each segment of
 * its path is folded the way the query is (Turkish letters flattened, lower case), so typing a
 * category's name still lists that category's actions — the Cascader matched every segment too.
 * The event segment is left out: the search already runs inside one event.
 */
export interface IActionPickerSearchEntry {
  node: IActionPickerNode;
  name: string;
  context: string[];
}

export function buildSearchIndex(scope: IActionPickerNode[]): IActionPickerSearchEntry[] {
  return collectAddableLeaves(scope).map((node: IActionPickerNode): IActionPickerSearchEntry => {
    const context: string[] = getNodeChain(node)
      .slice(0, -1)
      .filter((link: IActionPickerNode): boolean => link.kind !== EActionPickerNodeKind.event)
      .map((link: IActionPickerNode): string => normalizeCascaderSearchText(link.label));
    return { node, name: normalizeCascaderSearchText(node.label), context };
  });
}

export interface IActionPickerSearchResult {
  /** Every match, in display order. */
  matches: IActionPickerNode[];
  /** Matches per top-level category key, for the category column's counts. */
  countsByCategory: Map<string, number>;
}

function byLabel(a: IActionPickerNode, b: IActionPickerNode): number {
  return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
}

/**
 * A leaf matches when any one segment of its path does, by the subsequence rule of
 * `matchesCascaderSearch` (what the Cascader's per-segment Fzf filter reduced to), so "gtDok" still
 * finds "getDokumanById". The match set is the Cascader's; the ORDER is by how directly a leaf
 * matched — the action's whole name, the start of its name, anywhere in its name, inside a category
 * or folder name, then spread across the name, then spread across a folder — so a short query's scattered-letter hits never
 * push the actions that plainly contain it off the top. Each tier is sorted by name.
 */
export function searchActionPicker(index: IActionPickerSearchEntry[], query: string): IActionPickerSearchResult {
  const needle: string = normalizeCascaderSearchText(query.trim());
  const countsByCategory: Map<string, number> = new Map<string, number>();
  if (!needle) {
    return { matches: [], countsByCategory };
  }
  const tiers: IActionPickerNode[][] = [[], [], [], [], [], []];
  for (const entry of index) {
    if (entry.name === needle) {
      tiers[0].push(entry.node);
    } else if (entry.name.startsWith(needle)) {
      tiers[1].push(entry.node);
    } else if (entry.name.includes(needle)) {
      tiers[2].push(entry.node);
    } else if (entry.context.some((segment: string): boolean => segment.includes(needle))) {
      tiers[3].push(entry.node);
    } else if (matchesCascaderSearch(entry.name, needle)) {
      tiers[4].push(entry.node);
    } else if (entry.context.some((segment: string): boolean => matchesCascaderSearch(segment, needle))) {
      tiers[5].push(entry.node);
    }
  }
  const matches: IActionPickerNode[] = tiers.flatMap((tier: IActionPickerNode[]): IActionPickerNode[] =>
    tier.sort(byLabel)
  );
  for (const node of matches) {
    const category: IActionPickerNode | null = getCategoryAncestor(node);
    if (category) {
      countsByCategory.set(category.key, (countsByCategory.get(category.key) ?? 0) + 1);
    }
  }
  return { matches, countsByCategory };
}

/** The designer's generated-test-id spelling of a name: no whitespace, lower case. */
export function toActionPickerTestIdSlug(value: string): string {
  return value.replace(/\s+/g, "").toLowerCase();
}
